import type { ReactElement } from 'react'
import styles from './UserLocationDot.module.css'

export interface UserLocationDotProps {
  /**
   * Freeze the pulse at a deterministic keyframe (snapshot tests). When
   * set, the CSS animation is disabled and the rings render at their
   * **Full** keyframe (outer rings expanded). Production omits this so
   * the rings breathe via the infinite CSS animation.
   */
  readonly frozen?: boolean
}

/**
 * User-location pulse dot (Figma spec §1.5 — `UserLocationDot`
 * Rest/Mid/Full variant set). A 35×35 marker drawn natively as three
 * concentric circles, all `--mm-color-accent-geolocation` (#9DDC8F):
 *
 *   - Core 25×25 at (5,5), opacity 1 (static).
 *   - Ring 29×29 at (3,3), opacity 0.7.
 *   - Ring 35×35 at (0,0), opacity 0.4.
 *
 * The two outer rings breathe via a CSS `@keyframes … infinite`
 * (easeInOut, autoreverse) that scales + fades them around the static
 * core — the web analogue of iOS `repeatForever(autoreverses:)` /
 * Android `rememberInfiniteTransition`. `frozen` pins the Full keyframe
 * for deterministic snapshots (spec §7.5).
 */
export function UserLocationDot({ frozen = false }: UserLocationDotProps): ReactElement {
  const cls = [styles.root, frozen ? styles.frozen : ''].filter(Boolean).join(' ')
  return (
    <div className={cls} data-testid="user-location-dot" aria-hidden="true">
      <span className={`${styles.ring} ${styles.ring35}`} />
      <span className={`${styles.ring} ${styles.ring29}`} />
      <span className={`${styles.ring} ${styles.core}`} />
    </div>
  )
}
